import React, { useState } from 'react';
import { Card, Row, Col, Button, Select, Space, Table } from 'antd';
import { Line } from '@ant-design/charts';
import { useQuery } from '@tanstack/react-query';
import { dashboardService, prescriptionService } from '../../services';
import { DateFilter } from '../../types';
import { formatCurrency } from '../../utils/format';
import './DashboardPage.css';

const DashboardPage: React.FC = () => {
  const [dateFilter, setDateFilter] = useState<DateFilter>({ type: 'today' });
  const [selectedDoctor, setSelectedDoctor] = useState<string | undefined>();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats', dateFilter, selectedDoctor],
    queryFn: () => dashboardService.getStats(dateFilter, selectedDoctor),
  });

  const { data: prescriptions, isLoading: prescriptionsLoading } = useQuery({
    queryKey: ['dashboard-prescriptions', dateFilter],
    queryFn: () => prescriptionService.getList(),
  });

  const dateFilterButtons = [
    { key: 'today', label: 'Hôm nay' },
    { key: 'week', label: 'Tuần này' },
    { key: 'month', label: 'Tháng này' },
    { key: 'quarter', label: 'Quý này' },
    { key: 'year', label: 'Năm nay' },
    { key: 'custom', label: 'Tùy chọn', icon: '📅' },
  ];

  const chartConfig = stats?.revenueChart
    ? {
        data: stats.revenueChart,
        xField: 'time',
        yField: 'revenue',
        smooth: true,
        point: {
          size: 4,
          shape: 'circle',
        },
        color: '#385da4',
        yAxis: {
          label: {
            formatter: (value: number) => `${(value / 1000).toFixed(0)}k`,
          },
        },
      }
    : null;

  const columns = [
    {
      title: 'STT',
      dataIndex: 'index',
      key: 'index',
      width: 60,
    },
    {
      title: 'Mã đơn',
      dataIndex: 'code',
      key: 'code',
      render: (text: string) => <a>{text}</a>,
    },
    {
      title: 'Tên bác sĩ',
      dataIndex: 'doctorName',
      key: 'doctorName',
    },
    {
      title: 'Tên bệnh nhân',
      dataIndex: 'patientName',
      key: 'patientName',
    },
    {
      title: 'Chẩn đoán',
      dataIndex: 'diagnosis',
      key: 'diagnosis',
    },
    {
      title: 'Tổng tiền',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      align: 'right' as const,
      render: (amount: number) => formatCurrency(amount),
    },
    {
      title: 'Thuế',
      dataIndex: 'tax',
      key: 'tax',
      align: 'right' as const,
      render: (tax: number) => formatCurrency(tax),
    },
    {
      title: 'Tổng doanh thu',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      align: 'right' as const,
      render: (revenue: number) => formatCurrency(revenue),
    },
  ];

  const tableData = prescriptions?.slice(0, 5).map((p, index) => ({
    ...p,
    index: index + 1,
    key: p.id,
  }));

  const totalRow = {
    index: 'Tổng',
    code: '',
    doctorName: '',
    patientName: '',
    diagnosis: '',
    totalAmount: prescriptions?.reduce((sum, p) => sum + p.totalAmount, 0) || 0,
    tax: prescriptions?.reduce((sum, p) => sum + p.tax, 0) || 0,
    totalRevenue: prescriptions?.reduce((sum, p) => sum + p.totalRevenue, 0) || 0,
    key: 'total',
  };

  return (
    <div className="dashboard-page">
      <h1 className="page-title">Dashboard</h1>

      <div className="dashboard-filters">
        <Space wrap>
          {dateFilterButtons.map((btn) => (
            <Button
              key={btn.key}
              type={dateFilter.type === btn.key ? 'primary' : 'default'}
              onClick={() => setDateFilter({ type: btn.key as DateFilter['type'] })}
            >
              {btn.label} {btn.icon}
            </Button>
          ))}
        </Space>
        <Select
          placeholder="Chọn bác sĩ"
          style={{ width: 200 }}
          allowClear
          onChange={setSelectedDoctor}
        >
          <Select.Option value="1">Nguyễn Văn A</Select.Option>
          <Select.Option value="2">Nguyễn Thị B</Select.Option>
        </Select>
      </div>

      <Card className="revenue-card">
        <div className="revenue-summary">
          <span className="revenue-label">Doanh thu:</span>
          <span className="revenue-value">
            {stats ? formatCurrency(stats.revenue) : '0₫'}
          </span>
        </div>
      </Card>

      <Row gutter={16} style={{ marginTop: 24 }}>
        <Col span={16}>
          <Card title="Doanh thu" loading={statsLoading}>
            {chartConfig && <Line {...chartConfig} height={300} />}
          </Card>
        </Col>
        <Col span={8}>
          <Card title="Dịch vụ" loading={statsLoading}>
            <div className="services-list">
              {stats?.services.map((service, index) => (
                <div key={index} className="service-item">
                  <div className="service-name">{service.name}</div>
                  <div className="service-details">
                    <span>Số lượng: {service.quantity}</span>
                    <span className="service-amount">
                      Tổng tiền: {formatCurrency(service.totalAmount)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <Space>
            <span>Danh sách</span>
            <Select defaultValue="national-prescription" style={{ width: 200 }}>
              <Select.Option value="national-prescription">Đơn thuốc quốc gia</Select.Option>
            </Select>
          </Space>
        }
        style={{ marginTop: 24 }}
      >
        <Table
          columns={columns}
          dataSource={tableData ? [...tableData, totalRow] : []}
          loading={prescriptionsLoading}
          pagination={false}
          size="middle"
        />
      </Card>
    </div>
  );
};

export default DashboardPage;

